import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  ArrowLeft,
  Plus,
  ChevronRight,
  Settings,
  Calendar,
  Filter,
  X,
  ExternalLink,
  Users,
  UserPlus,
  UserMinus,
  BarChart3,
  Sparkles,
} from "lucide-react";
import Avatar from "./Avatar";
import InviteUserModal from "./InviteUserModal";
import { useProject } from "../contexts/ProjectContext";
import { useNotification } from "../contexts/NotificationContext";
import { useSocket } from "../contexts/SocketContext";
import { useUser } from "../contexts/UserContext";
import { useCardImageVisibility } from "../contexts/CardImageVisibilityContext";
import { useProjectActiveTimers } from "../hooks/useProjectActiveTimers";
import {
  cardAPI,
  columnAPI,
  timeEntryAPI,
  userAPI,
} from "../utils/api";
import ListColumn from "./ListColumn";
import CreateCardModal from "./CreateCardModal";
import ConfirmationModal from "./ConfirmationModal";
import CardModal from "./CardModal";
import EditProjectModal from "./EditProjectModal";
import FilterPanel from "./FilterPanel";
import MoveAllCardsModal from "./MoveAllCardsModal";
import AIExtractModal from "./AIExtractModal";
import SimpleQuillEditor from "./SimpleQuillEditor";
// import { stripHtmlTags } from "../utils/htmlUtils";
import {
  getProjectStatusColors,
  getCardStatusColors,
} from "../utils/statusColors";
import { TimeReportsModal } from "./TimeTracking";
import { features } from "../config/features";

// Horizontal Scroll Position Indicator Component (Jira-style).
// Self-subscribes to the scroll container (via containerRef) and holds its own
// scroll metrics in local state, so scrolling/dragging it never re-renders the
// board's column tree.
const HorizontalScrollIndicator = React.memo(
  ({ containerRef, revision, indicatorWidth = 150, onCanScrollMoreChange }) => {
    const indicatorRef = useRef(null);
    const isDraggingRef = useRef(false);
    const rafRef = useRef(null);
    const lastCanScrollMoreRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [metrics, setMetrics] = useState({
      scrollLeft: 0,
      scrollWidth: 0,
      clientWidth: 0,
    });

    // Subscribe to the container's scroll + resize and re-measure locally.
    useEffect(() => {
      const el = containerRef?.current;
      if (!el) return;

      const measure = () => {
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setMetrics({ scrollLeft, scrollWidth, clientWidth });
        const canScrollMore = scrollLeft < scrollWidth - clientWidth - 10;
        if (
          onCanScrollMoreChange &&
          canScrollMore !== lastCanScrollMoreRef.current
        ) {
          lastCanScrollMoreRef.current = canScrollMore;
          onCanScrollMoreChange(canScrollMore);
        }
      };

      const onScroll = () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(measure);
      };

      measure();
      el.addEventListener("scroll", onScroll, { passive: true });
      const ro =
        typeof ResizeObserver !== "undefined"
          ? new ResizeObserver(measure)
          : null;
      ro?.observe(el);

      return () => {
        el.removeEventListener("scroll", onScroll);
        ro?.disconnect();
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      };
      // `revision` (e.g. column count) forces a re-measure when content width changes.
    }, [containerRef, onCanScrollMoreChange, revision]);

    const { scrollLeft, scrollWidth, clientWidth } = metrics;
    const isScrollable = scrollWidth > clientWidth;
    if (!isScrollable) return null;

    const totalLines = 16; // Fine vertical stripes matching screenshot
    const viewportRatio = clientWidth / scrollWidth;
    const thumbWidth = Math.max(viewportRatio * indicatorWidth, 24);
    const maxScroll = scrollWidth - clientWidth;
    const scrollRatio = maxScroll > 0 ? scrollLeft / maxScroll : 0;
    const thumbLeft = scrollRatio * (indicatorWidth - thumbWidth);

    const applyScroll = (left, smooth) => {
      const el = containerRef?.current;
      if (!el) return;
      if (smooth) el.scrollTo({ left, behavior: "smooth" });
      else el.scrollLeft = left;
    };

    const handleMouseDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      isDraggingRef.current = true;
      setIsDragging(true);
      document.body.style.cursor = "grabbing";
      document.body.style.userSelect = "none";

      const startX = e.clientX;
      const startThumbLeft = thumbLeft;

      const handleMouseMove = (moveEvent) => {
        if (!isDraggingRef.current) return;
        const deltaX = moveEvent.clientX - startX;
        const newThumbLeft = Math.max(
          0,
          Math.min(startThumbLeft + deltaX, indicatorWidth - thumbWidth)
        );
        const newScrollRatio =
          indicatorWidth - thumbWidth > 0
            ? newThumbLeft / (indicatorWidth - thumbWidth)
            : 0;
        applyScroll(newScrollRatio * maxScroll, false);
      };

      const handleMouseUp = () => {
        isDraggingRef.current = false;
        setIsDragging(false);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    // Handle click on the track to jump to position
    const handleTrackClick = (e) => {
      if (e.target.classList.contains("scroll-thumb")) return;
      const rect = indicatorRef.current.getBoundingClientRect();
      const relativeX = e.clientX - rect.left - 8;
      const clickPosition = Math.max(
        0,
        Math.min(relativeX - thumbWidth / 2, indicatorWidth - thumbWidth)
      );
      const newScrollRatio =
        indicatorWidth - thumbWidth > 0
          ? clickPosition / (indicatorWidth - thumbWidth)
          : 0;
      applyScroll(newScrollRatio * maxScroll, true);
    };

    return (
      <div className="absolute right-5 bottom-12 z-20">
        <div
          ref={indicatorRef}
          onClick={handleTrackClick}
          className="relative bg-white dark:bg-slate-800 rounded-[6px] border border-gray-300 dark:border-slate-600 p-2 cursor-pointer shadow-lg"
          style={{
            width: `${indicatorWidth + 16}px`,
            height: "56px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          }}
        >
          {/* Vertical stripe lines */}
          <div className="flex gap-[2px] h-full w-full">
            {Array.from({ length: totalLines }).map((_, i) => (
              <div
                key={i}
                className="flex-1 bg-blue-100 dark:bg-slate-700 rounded-[2px]"
              />
            ))}
          </div>

          {/* Blue outline draggable thumb */}
          <div
            onMouseDown={handleMouseDown}
            className="scroll-thumb absolute top-2 bottom-2 border-2 border-blue-600 dark:border-blue-500 rounded-[4px] bg-transparent cursor-grab active:cursor-grabbing"
            style={{
              left: `${thumbLeft + 8}px`,
              width: `${thumbWidth}px`,
              transition: isDragging ? "none" : "left 0.1s ease-out",
            }}
          />
        </div>
      </div>
    );
  }
);

const ProjectBoard = () => {
  const { id, projectId, cardId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentProject, fetchProject, loading, removeProjectMember } =
    useProject();
  const { showToast } = useNotification();
  const { socket, joinProject, leaveProject } = useSocket();
  const { user } = useUser();
  const { showCardImages, toggleShowCardImages } = useCardImageVisibility();

  // Determine the actual project ID early
  const actualProjectId = projectId || id;
  
  const MAX_VISIBLE_MEMBER_AVATARS = 3;
  const headerMembers = useMemo(() => {
    if (!Array.isArray(currentProject?.members)) return [];
    const seenIds = new Set();
    return currentProject.members
      .map((member) => {
        if (!member) return null;
        if (member.user) return member.user;
        if (member.name) return member;
        return null;
      })
      .filter((user) => {
        if (!user) return false;
        const identifier = user._id || user.id || user.email || user.name;
        if (!identifier) return true;
        if (seenIds.has(identifier)) return false;
        seenIds.add(identifier);
        return true;
      });
  }, [currentProject?.members]);
  const visibleHeaderMembers = headerMembers.slice(
    0,
    MAX_VISIBLE_MEMBER_AVATARS
  );
  const extraMembersCount = Math.max(
    0,
    headerMembers.length - visibleHeaderMembers.length
  );
  const [cards, setCards] = useState([]);
  const [columns, setColumns] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("todo");
  const [showScrollButton, setShowScrollButton] = useState(false);
  const scrollContainerRef = useRef(null);
  const addColumnModalRef = useRef(null);
  const [loadingCards, setLoadingCards] = useState(true);
  const [loadingColumns, setLoadingColumns] = useState(true);
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [newColumnName, setNewColumnName] = useState("");
  const [newColumnDescription, setNewColumnDescription] = useState("");
  const [newColumnColor, setNewColumnColor] = useState("gray");
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showAIExtractModal, setShowAIExtractModal] = useState(false);

  // Card modal state
  const [selectedCard, setSelectedCard] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [draggingColumnId, setDraggingColumnId] = useState(null);
  const [dragOverColumnId, setDragOverColumnId] = useState(null);

  // Card drag-and-drop state
  const [activeCardId, setActiveCardId] = useState(null);

  // Configure sensors for drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before starting drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter state
  const [filteredCards, setFilteredCards] = useState([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [activeTimer, setActiveTimer] = useState(null);
  const activeTimerIntervalRef = useRef(null);

  // Get all active timers for the project (real-time via socket)
  const { activeTimers } = useProjectActiveTimers(actualProjectId, socket);

  // Time tracking modal state
  const [showTimeReportsModal, setShowTimeReportsModal] = useState(false);
  const [showMembersPopover, setShowMembersPopover] = useState(false);
  const membersBtnRef = useRef(null);
  const membersPopoverRef = useRef(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [confirmingUserId, setConfirmingUserId] = useState(null);
  const [showMoveAllCardsModal, setShowMoveAllCardsModal] = useState(false);
  const [sourceColumnForMove, setSourceColumnForMove] = useState(null);
  const [isMovingCards, setIsMovingCards] = useState(false);

  // Horizontal scroll state for Jira-style indicator
  // Toggled (flip-only) by HorizontalScrollIndicator so the board doesn't
  // re-render on every scroll frame.
  const handleCanScrollMoreChange = useCallback((canScrollMore) => {
    setShowScrollButton(canScrollMore);
  }, []);

  // Time period filter state
  const [timeframeFilter, setTimeframeFilter] = useState("thisMonth");
  const [totalHours, setTotalHours] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [loadingHours, setLoadingHours] = useState(false);
  const [showTimeframeDropdown, setShowTimeframeDropdown] = useState(false);
  const timeframeDropdownRef = useRef(null);


  // Close members popover on outside click
  useEffect(() => {
    if (!showMembersPopover) return;
    const handleClickOutside = (e) => {
      // Check if click is outside both the button AND the popover content
      const isOutsideButton =
        membersBtnRef.current && !membersBtnRef.current.contains(e.target);
      const isOutsidePopover =
        membersPopoverRef.current &&
        !membersPopoverRef.current.contains(e.target);

      if (isOutsideButton && isOutsidePopover) {
        setShowMembersPopover(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMembersPopover]);

  // Handle AI Extract modal via URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const modalParam = searchParams.get('modal');

    if (modalParam === 'ai-extract') {
      setShowAIExtractModal(true);
    } else {
      setShowAIExtractModal(false);
    }
  }, [location.search]);

  const handleRemoveMember = async (memberUserId) => {
    if (!currentProject?._id) return;
    try {
      setRemovingMemberId(memberUserId);
      await removeProjectMember(currentProject._id, memberUserId);
      await fetchProject(currentProject._id);
      showToast("Member removed from project", "success");
    } catch (error) {
      console.error("Error removing member:", error);
      showToast(
        error.response?.data?.message || "Failed to remove member",
        "error"
      );
    } finally {
      setRemovingMemberId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Format total seconds into HH:MM:SS format
  const formatHours = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return {
      hours,
      minutes,
      seconds,
      formatted: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    };
  };

  // Get date range based on selected timeframe
  const getTimeframeRange = (filter) => {
    const now = new Date();
    let startDate, endDate;

    switch (filter) {
      case "thisWeek": {
        const start = new Date(now);
        start.setDate(now.getDate() - now.getDay());
        start.setHours(0, 0, 0, 0);
        startDate = start;
        endDate = new Date();
        break;
      }
      case "thisMonth": {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate = start;
        endDate = new Date();
        break;
      }
      case "lastMonth": {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        end.setHours(23, 59, 59, 999);
        startDate = start;
        endDate = end;
        break;
      }
      case "last30Days": {
        const start = new Date(now);
        start.setDate(now.getDate() - 30);
        start.setHours(0, 0, 0, 0);
        startDate = start;
        endDate = new Date();
        break;
      }
      case "last90Days": {
        const start = new Date(now);
        start.setDate(now.getDate() - 90);
        start.setHours(0, 0, 0, 0);
        startDate = start;
        endDate = new Date();
        break;
      }
      case "overall":
      default: {
        startDate = new Date(0); // Beginning of time
        endDate = new Date();
        break;
      }
    }

    return { startDate, endDate };
  };

  // Calculate and fetch total hours for selected timeframe
  const calculateTotalHours = useCallback(async (filter) => {
    if (!actualProjectId || !features.timeTracking) return;
    
    setLoadingHours(true);
    try {
      const { startDate, endDate } = getTimeframeRange(filter);
      
      const response = await timeEntryAPI.getProjectEntries(actualProjectId, {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        summaryOnly: true,
      });

      if (response.data && response.data.stats) {
        setTotalHours(formatHours(response.data.stats.totalDuration || 0));
      }
    } catch (error) {
      console.error("Error calculating total hours:", error);
      setTotalHours({ hours: 0, minutes: 0, seconds: 0, formatted: "00:00:00" });
    } finally {
      setLoadingHours(false);
    }
  }, [actualProjectId]);


  // Track recently viewed projects (save server-side)
  useEffect(() => {
    if (!currentProject || !actualProjectId || !user) return;
    (async () => {
      try {
        await userAPI.recordRecentlyViewed(actualProjectId);
      } catch (e) {
        console.error("Failed to record recently viewed project", e);
      }
    })();
  }, [currentProject, actualProjectId, user]);

  useEffect(() => {
    if (actualProjectId) {
      fetchProject(actualProjectId);
      fetchCards();
      fetchColumns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualProjectId]);

  // Join/leave project room on mount/unmount
  useEffect(() => {
    if (actualProjectId && socket) {
      joinProject(actualProjectId);
      return () => {
        leaveProject(actualProjectId);
      };
    }
  }, [actualProjectId, socket, joinProject, leaveProject]);

  // Listen for Socket.IO events
  useEffect(() => {
    if (!socket) return;

    const handleCardCreated = (data) => {
      // Only update if not created by current user
      if (data.card && data.userId !== user?._id && data.userId !== user?.id) {
        setCards((prev) => [...prev, data.card]);
        setFilteredCards((prev) => [...prev, data.card]);
        showToast(`New card "${data.card.title}" created!`, "success");
      }
    };

    const handleCardUpdated = (data) => {
      // Only update if not updated by current user
      if (data.card && data.userId !== user?._id && data.userId !== user?.id) {
        setCards((prev) =>
          prev.map((card) => (card._id === data.card._id ? data.card : card))
        );
        setFilteredCards((prev) =>
          prev.map((card) => (card._id === data.card._id ? data.card : card))
        );
        // Update selected card if it's the one being updated
        if (selectedCard && selectedCard._id === data.card._id) {
          setSelectedCard(data.card);
        }
      }
    };

    // Dedicated (not the generic full-replace handleCardUpdated above): a new
    // comment's full card payload carries comments[]/activityLog[] that the
    // board deliberately keeps out of its slim card state, so we merge in
    // only the two relevant slim fields instead of replacing the whole card.
    const handleCardCommentAdded = (data) => {
      if (!data.card) return;
      const myId = String(user?._id || user?.id || "");
      const isOwnComment = String(data.userId) === myId;
      const patch = (card) => {
        if (card._id !== data.card._id) return card;
        // Only light the dot for someone who has actually opened this card
        // before (has a readBy entry) — matches the server-side rule that a
        // never-opened card relies on the existing blue unread border
        // instead, so this live update can't contradict what a refresh would
        // show.
        const iHaveOpenedIt = (card.readBy || []).some(
          (r) => String(r.user?._id || r.user) === myId
        );
        return {
          ...card,
          commentCount: data.card.commentCount ?? (card.commentCount ?? 0),
          hasUnseenComments:
            !isOwnComment && iHaveOpenedIt ? true : card.hasUnseenComments,
        };
      };
      setCards((prev) => prev.map(patch));
      setFilteredCards((prev) => prev.map(patch));
    };

    const handleCardArchived = (data) => {
      if (data.card) {
        setCards((prev) =>
          prev.map((card) =>
            card._id === data.card._id
              ? { ...card, isArchived: true, status: "archive" }
              : card
          )
        );
        setFilteredCards((prev) =>
          prev.map((card) =>
            card._id === data.card._id
              ? { ...card, isArchived: true, status: "archive" }
              : card
          )
        );
        // Close modal if the archived card was selected
        if (selectedCard && selectedCard._id === data.card._id) {
          setShowCardModal(false);
          setSelectedCard(null);
        }
      }
    };

    const handleCardRestoredSocket = (data) => {
      // Only handle socket events from other users, not our own actions
      if (data.card && data.userId !== user?._id && data.userId !== user?.id) {
        // Refresh cards when another user restores a card
        cardAPI
          .getCards(actualProjectId, true)
          .then((response) => {
            const fetchedCards = response.data.cards || [];
            setCards(fetchedCards);
            setFilteredCards(fetchedCards);

            // Update selected card if it's the one being restored
            if (selectedCard && selectedCard._id === data.card._id) {
              setSelectedCard(data.card);
            }
          })
          .catch((error) => {
            console.error("Error fetching cards:", error);
          });
      }
    };

    const handleCardStatusChanged = (data) => {
      if (data.card) {
        setCards((prev) =>
          prev.map((card) =>
            card._id === data.card._id
              ? { ...card, status: data.card.status }
              : card
          )
        );
        setFilteredCards((prev) =>
          prev.map((card) =>
            card._id === data.card._id
              ? { ...card, status: data.card.status }
              : card
          )
        );
        // Update selected card if it's the one being updated
        if (selectedCard && selectedCard._id === data.card._id) {
          setSelectedCard((prev) => ({ ...prev, status: data.card.status }));
        }
      }
    };

    const handleColumnCreated = (data) => {
      // Only update if not created by current user
      if (
        data.column &&
        data.userId !== user?._id &&
        data.userId !== user?.id
      ) {
        setColumns((prev) => [...prev, data.column]);
        showToast(`New column "${data.column.name}" created!`, "success");
      }
    };

    const handleColumnUpdated = (data) => {
      if (data.column) {
        setColumns((prev) =>
          prev.map((col) => (col._id === data.column._id ? data.column : col))
        );
      }
    };

    const handleCardsBulkMoved = (data) => {
      if (data.projectId === actualProjectId) {
        // Refresh cards to get updated data
        fetchCards();
        if (data.userId !== user?._id && data.userId !== user?.id) {
          showToast(
            `${data.cardCount} card(s) moved from one column to another`,
            "info"
          );
        }
      }
    };

    const currentUserId = user?._id || user?.id;

    const handleTimerStarted = (data) => {
      if (!data) return;
      if (data.userId === currentUserId) {
        const startedAt = data.startedAt || Date.now();
        setActiveTimer({
          cardId: data.cardId,
          startedAt: new Date(startedAt).getTime(),
          baseSeconds: 0,
          elapsedSeconds: 0,
          isRunning: true,
        });
      }

      if (data.cardId && data.activity) {
        setSelectedCard((prev) =>
          prev && prev._id === data.cardId
            ? {
                ...prev,
                activityLog: [data.activity, ...(prev.activityLog || [])],
              }
            : prev
        );
        setCards((prev) =>
          prev.map((card) =>
            card._id === data.cardId
              ? {
                  ...card,
                  activityLog: [data.activity, ...(card.activityLog || [])],
                }
              : card
          )
        );
        setFilteredCards((prev) =>
          prev.map((card) =>
            card._id === data.cardId
              ? {
                  ...card,
                  activityLog: [data.activity, ...(card.activityLog || [])],
                }
              : card
          )
        );
      }
    };

    const handleTimerStopped = (data) => {
      if (!data) return;
      if (data.userId === currentUserId) {
        setActiveTimer((prev) =>
          prev && prev.cardId === data.cardId ? null : prev
        );
      }

      if (data.cardId) {
        setSelectedCard((prev) =>
          prev && prev._id === data.cardId
            ? {
                ...prev,
                totalTimeSpent:
                  data.cardTotalTime !== undefined
                    ? data.cardTotalTime
                    : prev.totalTimeSpent,
                ...(data.activity
                  ? {
                      activityLog: [
                        data.activity,
                        ...(prev.activityLog || []),
                      ],
                    }
                  : {}),
              }
            : prev
        );

        if (data.cardTotalTime !== undefined || data.activity) {
          setCards((prev) =>
            prev.map((card) =>
              card._id === data.cardId
                ? {
                    ...card,
                    totalTimeSpent:
                      data.cardTotalTime !== undefined
                        ? data.cardTotalTime
                        : card.totalTimeSpent,
                    ...(data.activity
                      ? {
                          activityLog: [
                            data.activity,
                            ...(card.activityLog || []),
                          ],
                        }
                      : {}),
                  }
                : card
            )
          );
          setFilteredCards((prev) =>
            prev.map((card) =>
              card._id === data.cardId
                ? {
                    ...card,
                    totalTimeSpent:
                      data.cardTotalTime !== undefined
                        ? data.cardTotalTime
                        : card.totalTimeSpent,
                    ...(data.activity
                      ? {
                          activityLog: [
                            data.activity,
                            ...(card.activityLog || []),
                          ],
                        }
                      : {}),
                  }
                : card
            )
          );
        }
      }
    };

    const handleTimerDiscarded = (data) => {
      if (!data || data.userId !== currentUserId) return;
      setActiveTimer((prev) =>
        prev && prev.cardId === data.cardId ? null : prev
      );
    };

    const handleCardsReordered = (data) => {
      if (data.cards && data.userId !== user?._id && data.userId !== user?.id) {
        const updatedCardsMap = {};
        data.cards.forEach((c) => {
          updatedCardsMap[c._id] = c;
        });

        const patchCard = (c) => {
          const updatedCard = updatedCardsMap[c._id];
          if (updatedCard) {
            return {
              ...c,
              ...updatedCard,
              commentCount: updatedCard.commentCount ?? c.commentCount,
            };
          }
          return c;
        };

        setCards((prev) => prev.map(patchCard));
        setFilteredCards((prev) => prev.map(patchCard));

        if (selectedCard) {
          const updatedSelectedCard = updatedCardsMap[selectedCard._id];
          if (updatedSelectedCard) {
            setSelectedCard((prev) => ({
              ...prev,
              ...updatedSelectedCard,
              commentCount: updatedSelectedCard.commentCount ?? prev.commentCount,
            }));
          }
        }
      }
    };

    socket.on("card-created", handleCardCreated);
    socket.on("card-updated", handleCardUpdated);
    socket.on("card-archived", handleCardArchived);
    socket.on("card-restored", handleCardRestoredSocket);
    socket.on("card-status-changed", handleCardStatusChanged);
    socket.on("card-completion-toggled", handleCardUpdated);
    socket.on("card-read", handleCardUpdated);
    socket.on("card-comment-added", handleCardCommentAdded);
    socket.on("card-user-assigned", handleCardUpdated);
    socket.on("card-user-unassigned", handleCardUpdated);
    socket.on("card-label-added", handleCardUpdated);
    socket.on("card-label-removed", handleCardUpdated);
    socket.on("card-attachment-added", handleCardUpdated);
    socket.on("card-attachment-removed", handleCardUpdated);
    socket.on("card-files-uploaded", handleCardUpdated);
    socket.on("column-created", handleColumnCreated);
    socket.on("column-updated", handleColumnUpdated);
    socket.on("cards-bulk-moved", handleCardsBulkMoved);
    socket.on("cards-reordered", handleCardsReordered);
    socket.on("timer-started", handleTimerStarted);
    socket.on("timer-stopped", handleTimerStopped);
    socket.on("timer-discarded", handleTimerDiscarded);

    return () => {
      socket.off("card-created", handleCardCreated);
      socket.off("card-updated", handleCardUpdated);
      socket.off("card-archived", handleCardArchived);
      socket.off("card-restored", handleCardRestoredSocket);
      socket.off("card-status-changed", handleCardStatusChanged);
      socket.off("card-completion-toggled", handleCardUpdated);
      socket.off("card-read", handleCardUpdated);
      socket.off("card-comment-added", handleCardCommentAdded);
      socket.off("card-user-assigned", handleCardUpdated);
      socket.off("card-user-unassigned", handleCardUpdated);
      socket.off("card-label-added", handleCardUpdated);
      socket.off("card-label-removed", handleCardUpdated);
      socket.off("card-attachment-added", handleCardUpdated);
      socket.off("card-attachment-removed", handleCardUpdated);
      socket.off("card-files-uploaded", handleCardUpdated);
      socket.off("column-created", handleColumnCreated);
      socket.off("column-updated", handleColumnUpdated);
      socket.off("cards-bulk-moved", handleCardsBulkMoved);
      socket.off("cards-reordered", handleCardsReordered);
      socket.off("timer-started", handleTimerStarted);
      socket.off("timer-stopped", handleTimerStopped);
      socket.off("timer-discarded", handleTimerDiscarded);
    };
  }, [socket, selectedCard, showToast, user, actualProjectId]);

  // Handle click outside to close add column modal
  useEffect(() => {
    if (!showAddColumnModal) return;

    const handleClickOutside = (event) => {
      if (
        addColumnModalRef.current &&
        !addColumnModalRef.current.contains(event.target)
      ) {
        setShowAddColumnModal(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddColumnModal]);

  // Handle card modal opening from URL
  useEffect(() => {
    if (cardId && cards.length > 0) {
      const card = cards.find((c) => c._id === cardId);
      if (card) {
        setSelectedCard(card);
        setShowCardModal(true);
      }
    } else if (!cardId && showCardModal) {
      // If cardId is removed from URL, close the modal
      setShowCardModal(false);
      setSelectedCard(null);
    }
  }, [cardId, cards, showCardModal]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      // When user navigates back/forward, check if we should close the modal
      if (!cardId && showCardModal) {
        setShowCardModal(false);
        setSelectedCard(null);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [cardId, showCardModal]);

  const fetchCards = async () => {
    try {
      const response = await cardAPI.getCards(actualProjectId, true); // Include archived cards
      const fetchedCards = response.data.cards || [];
      setCards(fetchedCards);
      setFilteredCards(fetchedCards); // Initialize filtered cards
    } catch (error) {
      console.error("Error fetching cards:", error);
      console.error("Error details:", error.response?.data);
      showToast("Failed to load cards", "error");
    } finally {
      setLoadingCards(false);
    }
  };

  const fetchColumns = async () => {
    try {
      const response = await columnAPI.getColumns(actualProjectId);
      setColumns(response.data.columns || []);
    } catch (error) {
      console.error("Error fetching columns:", error);
      console.error("Error details:", error.response?.data);
      showToast("Failed to load columns", "error");
    } finally {
      setLoadingColumns(false);
    }
  };

  const handleCardCreated = (newCard) => {
    setCards((prev) => [...prev, newCard]);
    setFilteredCards((prev) => [...prev, newCard]);
    showToast("Card created successfully!", "success");
  };

  const handleCancelFilter = () => {
    setFilteredCards(cards);
    setHasActiveFilters(false);
    setFilterResetKey((prev) => prev + 1);
    showToast("Filters cleared", "success");
  };

  const handleCardUpdated = useCallback((updatedCard) => {
    const mergeCardUpdate = (card) => ({
      ...card,
      ...updatedCard,
      commentCount: updatedCard.commentCount ?? card.commentCount ?? 0,
    });
    setCards((prev) =>
      prev.map((card) => (card._id === updatedCard._id ? mergeCardUpdate(card) : card))
    );
    setFilteredCards((prev) =>
      prev.map((card) => (card._id === updatedCard._id ? mergeCardUpdate(card) : card))
    );
    // Update selected card if it's the one being updated
    setSelectedCard((prev) =>
      prev && prev._id === updatedCard._id ? mergeCardUpdate(prev) : prev
    );
  }, []);

  const handleCardDeleted = useCallback(
    (cardId) => {
      // Update the card to be archived instead of removing it
      setCards((prev) =>
        prev.map((card) =>
          card._id === cardId
            ? { ...card, isArchived: true, status: "archive" }
            : card
        )
      );
      setFilteredCards((prev) =>
        prev.map((card) =>
          card._id === cardId
            ? { ...card, isArchived: true, status: "archive" }
            : card
        )
      );
      showToast("Card archived successfully!", "success");
      // Close modal if the archived card was selected
      setSelectedCard((prev) => {
        if (prev && prev._id === cardId) {
          setShowCardModal(false);
          navigate(`/project/${actualProjectId}`);
          return null;
        }
        return prev;
      });
    },
    [actualProjectId, navigate, showToast]
  );

  const handleCardPermanentlyDeleted = async (cardId) => {
    try {
      // Remove the card from state permanently (optimistic update)
      setCards((prev) => prev.filter((card) => card._id !== cardId));
      setFilteredCards((prev) => prev.filter((card) => card._id !== cardId));

      // Refresh cards to ensure sync with server
      await fetchCards();

      // Close modal if the deleted card was selected
      if (selectedCard && selectedCard._id === cardId) {
        setShowCardModal(false);
        setSelectedCard(null);
        // Navigate back to project view
        navigate(`/project/${actualProjectId}`);
      }
    } catch (error) {
      console.error("Error handling card deletion:", error);
      // Refresh cards as fallback
      await fetchCards();
    }
  };

  const handleCardRestored = useCallback(async (cardId, restoredCard) => {
    try {
      // Refresh cards to get updated data (API is already called in CardModal)
      await fetchCards();

      // Update selected card if it's the one being restored
      setSelectedCard((prev) => {
        if (prev && prev._id === cardId) {
          if (restoredCard) {
            return restoredCard;
          }
          return {
            ...prev,
            isArchived: false,
            archivedAt: null,
            archivedBy: null,
            status: prev.originalStatus || "todo",
          };
        }
        return prev;
      });
    } catch (error) {
      console.error("Error handling card restoration:", error);
    }
  }, []);

  const handleStatusChange = useCallback(
    async (cardId, newStatus) => {
      try {
        await cardAPI.updateStatus(cardId, { status: newStatus });
        setCards((prev) =>
          prev.map((card) =>
            card._id === cardId ? { ...card, status: newStatus } : card
          )
        );
        setFilteredCards((prev) =>
          prev.map((card) =>
            card._id === cardId ? { ...card, status: newStatus } : card
          )
        );
        // Update selected card if it's the one being updated
        setSelectedCard((prev) =>
          prev && prev._id === cardId ? { ...prev, status: newStatus } : prev
        );
      } catch (error) {
        console.error("Error updating card status:", error);
        showToast("Failed to update card status", "error");
      }
    },
    [showToast]
  );

  // Handle card drag end (for drag-and-drop)
  const handleCardDragEnd = async (event) => {
    const { active, over } = event;
    setActiveCardId(null);

    if (!over || !active) return;

    const cardId = active.id;
    const sourceStatus = active.data.current?.status;
    const targetStatus = over.data.current?.status || over.id;

    // If dropped on a column (not another card)
    if (over.data.current?.droppableType === "column") {
      const newStatus = targetStatus;

      // Prevent dropping cards into archive column
      if (newStatus === "archive") {
        showToast(
          "Cards cannot be moved to Archive. Use the Archive button in the card details.",
          "warning"
        );
        return;
      }

      // Get cards in the target column
      const targetCards = getCardsByStatus(newStatus);

      // If moving to a different column, add to end
      if (sourceStatus !== newStatus) {
        const newOrder = targetCards.length;

        // Update locally first for instant feedback
        const updatedCards = cards.map((card) => {
          if (card._id === cardId) {
            return { ...card, status: newStatus, order: newOrder };
          }
          return card;
        });
        setCards(updatedCards);
        setFilteredCards(updatedCards);

        // Update backend
        try {
          const cardOrders = [
            { cardId: String(cardId), order: newOrder, status: newStatus },
          ];
          const response = await cardAPI.reorderCards(cardOrders);
          if (response.data.success && response.data.cards) {
            // Update with server response
            const updatedCard = response.data.cards[0];
            setCards((prev) =>
              prev.map((card) => (card._id === cardId ? updatedCard : card))
            );
            setFilteredCards((prev) =>
              prev.map((card) => (card._id === cardId ? updatedCard : card))
            );
          }
        } catch (error) {
          console.error("Error reordering card:", error);
          const errorMessage =
            error.response?.data?.message || "Failed to move card";
          showToast(errorMessage, "error");
          // Revert on error
          fetchCards();
        }
      }
      return;
    }

    // If dropped on another card
    const overCardId = over.id;
    if (cardId === overCardId) return;

    const sourceCards = getCardsByStatus(sourceStatus);
    const targetCards = getCardsByStatus(targetStatus);
    const sourceIndex = sourceCards.findIndex((c) => c._id === cardId);
    const overIndex = targetCards.findIndex((c) => c._id === overCardId);

    if (sourceIndex === -1 || overIndex === -1) return;

    // Prevent any drag-and-drop operations in archive column
    if (sourceStatus === "archive" || targetStatus === "archive") {
      showToast("Unable to move the card to the Archive list.", "warning");
      return;
    }

    // Same column reordering
    if (sourceStatus === targetStatus) {
      const reorderedCards = arrayMove(sourceCards, sourceIndex, overIndex);

      // Update locally first
      const updatedCards = cards.map((card) => {
        const newIndex = reorderedCards.findIndex((c) => c._id === card._id);
        if (newIndex !== -1 && card.status === sourceStatus) {
          return { ...card, order: newIndex };
        }
        return card;
      });
      setCards(updatedCards);
      setFilteredCards(updatedCards);

      // Update backend
      try {
        const cardOrders = reorderedCards.map((card, index) => ({
          cardId: String(card._id),
          order: index,
        }));
        const response = await cardAPI.reorderCards(cardOrders);
        if (response.data.success && response.data.cards) {
          // Update with server response
          const updatedCardsMap = {};
          response.data.cards.forEach((card) => {
            updatedCardsMap[card._id] = card;
          });
          setCards((prev) =>
            prev.map((card) => updatedCardsMap[card._id] ? { ...card, ...updatedCardsMap[card._id], commentCount: updatedCardsMap[card._id].commentCount ?? card.commentCount ?? 0 } : card)
          );
          setFilteredCards((prev) =>
            prev.map((card) => updatedCardsMap[card._id] ? { ...card, ...updatedCardsMap[card._id], commentCount: updatedCardsMap[card._id].commentCount ?? card.commentCount ?? 0 } : card)
          );
        }
      } catch (error) {
        console.error("Error reordering cards:", error);
        const errorMessage =
          error.response?.data?.message || "Failed to reorder cards";
        showToast(errorMessage, "error");
        fetchCards();
      }
    } else {
      // Moving between columns

      // Prevent dropping cards into archive column
      if (targetStatus === "archive") {
        showToast(
          "Cards cannot be moved to Archive. Use the Archive button in the card details.",
          "warning"
        );
        return;
      }

      const newTargetCards = [...targetCards];
      newTargetCards.splice(overIndex, 0, sourceCards[sourceIndex]);

      // Update locally first
      const updatedCards = cards.map((card) => {
        if (card._id === cardId) {
          return {
            ...card,
            status: targetStatus,
            order: overIndex,
            movedAt: new Date().toISOString(),
          };
        }
        // Update orders in target column
        if (card.status === targetStatus) {
          const newIndex = newTargetCards.findIndex((c) => c._id === card._id);
          if (newIndex !== -1) {
            return { ...card, order: newIndex };
          }
        }
        // Update orders in source column
        if (card.status === sourceStatus && card._id !== cardId) {
          const newIndex = sourceCards
            .filter((c) => c._id !== cardId)
            .findIndex((c) => c._id === card._id);
          if (newIndex !== -1) {
            return { ...card, order: newIndex };
          }
        }
        return card;
      });
      setCards(updatedCards);
      setFilteredCards(updatedCards);

      // Update backend
      try {
        const cardOrders = [];
        // Add moved card
        cardOrders.push({
          cardId: String(cardId),
          order: overIndex,
          status: targetStatus,
        });
        // Add all target column cards
        newTargetCards.forEach((card, index) => {
          if (card._id !== cardId) {
            cardOrders.push({ cardId: String(card._id), order: index });
          }
        });
        const response = await cardAPI.reorderCards(cardOrders);
        if (response.data.success && response.data.cards) {
          // Update with server response
          const updatedCardsMap = {};
          response.data.cards.forEach((card) => {
            updatedCardsMap[card._id] = card;
          });
          setCards((prev) =>
            prev.map((card) => updatedCardsMap[card._id] ? { ...card, ...updatedCardsMap[card._id], commentCount: updatedCardsMap[card._id].commentCount ?? card.commentCount ?? 0 } : card)
          );
          setFilteredCards((prev) =>
            prev.map((card) => updatedCardsMap[card._id] ? { ...card, ...updatedCardsMap[card._id], commentCount: updatedCardsMap[card._id].commentCount ?? card.commentCount ?? 0 } : card)
          );
        }
      } catch (error) {
        console.error("Error moving card:", error);
        const errorMessage =
          error.response?.data?.message || "Failed to move card";
        showToast(errorMessage, "error");
        fetchCards();
      }
    }
  };

  // Card modal handlers
  const handleCardClick = useCallback(
    (card) => {
      setSelectedCard(card);
      setShowCardModal(true);
      // Update URL to include card ID
      navigate(`/project/${actualProjectId}/card/${card._id}`);
    },
    [actualProjectId, navigate]
  );

  const handleCardModalClose = () => {
    setShowCardModal(false);
    setSelectedCard(null);
    // Navigate back to project view
    navigate(`/project/${actualProjectId}`);
  };

  const handleNavigateCard = (card) => {
    setSelectedCard(card);
    // Update URL to include card ID
    navigate(`/project/${actualProjectId}/card/${card._id}`);
  };

  // Active timer helpers
  const syncElapsed = useCallback((startedAt) => {
    return Math.max(
      0,
      Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000)
    );
  }, []);

  const handleTimerStart = useCallback(
    (cardId, startedAt = Date.now(), initialElapsed = 0) => {
      const baseSeconds = Math.max(initialElapsed || 0, 0);
      setActiveTimer({
        cardId,
        startedAt: startedAt instanceof Date ? startedAt.getTime() : startedAt,
        baseSeconds,
        elapsedSeconds: baseSeconds + syncElapsed(startedAt),
        isRunning: true,
      });
    },
    [syncElapsed]
  );

  const handleTimerStop = useCallback((cardId) => {
    setActiveTimer((prev) =>
      prev && prev.cardId === cardId ? null : prev
    );
  }, []);

  // Keep elapsed seconds ticking while a timer is active
  useEffect(() => {
    if (features.timeTracking && activeTimer?.isRunning && activeTimer.startedAt) {
      if (activeTimerIntervalRef.current) {
        clearInterval(activeTimerIntervalRef.current);
      }
      activeTimerIntervalRef.current = setInterval(() => {
        setActiveTimer((prev) => {
          if (!prev?.isRunning || !prev.startedAt) return prev;
          const baseSeconds =
            typeof prev.baseSeconds === "number"
              ? prev.baseSeconds
              : typeof prev.elapsedSeconds === "number"
              ? prev.elapsedSeconds
              : 0;
          const elapsedSeconds =
            baseSeconds + syncElapsed(prev.startedAt);
          if (elapsedSeconds === prev.elapsedSeconds) return prev;
          return { ...prev, elapsedSeconds };
        });
      }, 1000);
    } else if (activeTimerIntervalRef.current) {
      clearInterval(activeTimerIntervalRef.current);
      activeTimerIntervalRef.current = null;
    }

    return () => {
      if (activeTimerIntervalRef.current) {
        clearInterval(activeTimerIntervalRef.current);
        activeTimerIntervalRef.current = null;
      }
    };
  }, [activeTimer?.isRunning, activeTimer?.startedAt, syncElapsed]);

  // Load any active timer for the current user on mount
  useEffect(() => {
    if (!features.timeTracking) return;

    const loadActiveTimer = async () => {
      try {
        const response = await timeEntryAPI.getActiveTimer();
        if (response.data?.success && response.data.activeTimer) {
          const timer = response.data.activeTimer;
          const startedAt = timer.startedAt || new Date().toISOString();
          const estimatedBase = Math.max(
            (timer.elapsedSeconds || 0) - syncElapsed(startedAt),
            0
          );
          handleTimerStart(
            timer.card?._id || timer.card,
            startedAt,
            estimatedBase
          );
        } else {
          setActiveTimer(null);
        }
      } catch (error) {
        console.error("Error loading active timer:", error);
      }
    };

    loadActiveTimer();
  }, [handleTimerStart]);

  // Memoize cards by status to prevent unnecessary re-renders
  const cardsByStatus = useMemo(() => {
    const result = {};

    // Group cards by status
    columns.forEach((column) => {
      let statusCards;
      if (column.status === "archive") {
        statusCards = filteredCards.filter((card) => card.isArchived === true);
      } else {
        statusCards = filteredCards.filter(
          (card) => card.status === column.status && card.isArchived !== true
        );
      }
      // Keep the current relative order for cards with the same order value.
      // This prevents a simple edit/save from moving the card to the top of the column.
      result[column.status] = statusCards
        .map((card, index) => ({ card, index }))
        .sort((a, b) => {
          const orderA = a.card.order ?? 0;
          const orderB = b.card.order ?? 0;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          return a.index - b.index;
        })
        .map(({ card }) => card);
    });

    return result;
  }, [filteredCards, columns]);

  const getCardsByStatus = (status) => {
    return cardsByStatus[status] || [];
  };

  const getColumnConfig = (column) => {
    // Use card status colors for better design consistency
    const cardStatusColors = getCardStatusColors(column.status, column?.color);

    return {
      title: column?.name,
      color: column?.color,
      bgColor: cardStatusColors?.bgColor,
      borderColor: cardStatusColors?.borderColor,
      textColor: cardStatusColors?.textColor,
    };
  };

  // Drag and Drop for columns
  const handleColumnDragStart = (columnId) => {
    setDraggingColumnId(columnId);
    // Improve UX cursor on drag
    try {
      if (window?.event?.dataTransfer) {
        window.event.dataTransfer.effectAllowed = "move";
      }
    } catch (_) { }
  };

  const handleColumnDragOver = (e) => {
    // Allow dropping by preventing default
    e.preventDefault();
  };

  const handleColumnDrop = async (targetColumnId) => {
    if (!draggingColumnId || draggingColumnId === targetColumnId) return;

    // Compute new order locally for instant UI feedback
    const nonArchiveColumns = columns.filter((c) => c.status !== "archive");
    const archiveColumn = columns.find((c) => c.status === "archive");

    const currentIndex = nonArchiveColumns.findIndex(
      (c) => (c._id || c.status) === draggingColumnId
    );
    const targetIndex = nonArchiveColumns.findIndex(
      (c) => (c._id || c.status) === targetColumnId
    );

    if (currentIndex === -1 || targetIndex === -1) {
      setDraggingColumnId(null);
      return;
    }

    const reordered = [...nonArchiveColumns];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const nextColumns = archiveColumn
      ? [...reordered, archiveColumn]
      : reordered;
    setColumns(nextColumns);

    // Persist order (send only non-archive column ids)
    try {
      const orderedIds = reordered.map((c) => c._id);
      await columnAPI.reorderColumns(actualProjectId, orderedIds);
    } catch (error) {
      console.error("Failed to persist column order:", error);
      showToast("Failed to save column order", "error");
      // Refetch to resync with server state
      fetchColumns();
    } finally {
      setDraggingColumnId(null);
      setDragOverColumnId(null);
    }
  };

  const handleColumnDragEnd = () => {
    setDraggingColumnId(null);
    setDragOverColumnId(null);
  };

  const handleUpdateColumn = async (status, updates) => {
    try {
      // Find the column
      const column = columns.find((col) => col.status === status);
      if (!column) {
        showToast("Column not found", "error");
        return;
      }

      let apiUpdates = { ...updates };
      
      // DO NOT update status here because:
      // 1. Backend doesn't support updating status yet
      // 2. If status changes in frontend but not backend, cards disappear until refresh
      // 3. Default columns MUST keep their status (todo, doing, etc.)
      
      const response = await columnAPI.updateColumn(id, column._id, apiUpdates);

      if (response.data.success) {
        // Update existing column
        setColumns((prev) =>
          prev.map((col) =>
            col._id === column._id
              ? {
                ...col,
                ...response.data.column
              }
              : col
          )
        );

        showToast("Column updated successfully!", "success");
      }
    } catch (error) {
      console.error("Error updating column:", error);
      showToast("Failed to update column", "error");
    }
  };

  const handleColumnDelete = (status) => {
    // Find the column
    const column = columns.find((col) => col.status === status);
    if (!column || column.isDefault) {
      showToast("Cannot delete default columns", "error");
      return;
    }

    setColumnToDelete(column);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteColumn = async () => {
    if (!columnToDelete) return;

    try {
      const response = await columnAPI.deleteColumn(id, columnToDelete._id);
      if (response.data.success) {
        setColumns((prev) =>
          prev.filter((col) => col._id !== columnToDelete._id)
        );
        showToast("Column deleted successfully!", "success");
      }
    } catch (error) {
      console.error("Error deleting column:", error);
      showToast("Failed to delete column", "error");
    } finally {
      setShowDeleteConfirm(false);
      setColumnToDelete(null);
    }
  };

  const handleAddColumn = async () => {
    if (!newColumnName.trim()) {
      showToast("Column name is required", "error");
      return;
    }

    try {
      const response = await columnAPI.createColumn(id, {
        name: newColumnName.trim(),
        description: newColumnDescription,
        color: newColumnColor,
      });

      if (response.data.success) {
        setColumns((prev) => [...prev, response.data.column]);
        setNewColumnName("");
        setNewColumnDescription("");
        setNewColumnColor("gray");
        setShowAddColumnModal(false);
        showToast("Column created successfully!", "success");
      }
    } catch (error) {
      console.error("Error creating column:", error);
      showToast("Failed to create column", "error");
    }
  };

  const handleAddCardToColumn = (status) => {
    setSelectedStatus(status);
    setShowCreateModal(true);
  };

  const handleMoveAllCards = (sourceStatus) => {
    const column = columns.find((col) => col.status === sourceStatus);
    if (column) {
      setSourceColumnForMove(column);
      setShowMoveAllCardsModal(true);
    }
  };

  const handleConfirmMoveAllCards = async (targetColumnId) => {
    if (!sourceColumnForMove || !targetColumnId) return;

    try {
      setIsMovingCards(true);
      const targetColumn = columns.find(
        (col) => col._id === targetColumnId || col.status === targetColumnId
      );

      if (!targetColumn) {
        showToast("Target column not found", "error");
        return;
      }

      const response = await cardAPI.moveAllCards(
        actualProjectId,
        sourceColumnForMove.status,
        targetColumn.status
      );

      if (response.data.success) {
        // Refresh cards to get updated data
        await fetchCards();
        showToast(
          `Successfully moved ${response.data.movedCount} card(s)`,
          "success"
        );
        setShowMoveAllCardsModal(false);
        setSourceColumnForMove(null);
      }
    } catch (error) {
      console.error("Error moving all cards:", error);
      showToast(
        error.response?.data?.message || "Failed to move cards",
        "error"
      );
    } finally {
      setIsMovingCards(false);
    }
  };

  const scrollToEnd = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: scrollContainerRef.current.scrollWidth,
        behavior: "smooth",
      });
    }
  };

  const handleEditProject = () => {
    // Open modal and update the URL so refreshing preserves modal state
    if (actualProjectId) {
      navigate(`/project/${actualProjectId}/edit`);
    }
    setShowEditProjectModal(true);
  };

  // Keep edit modal state in sync with the URL so refreshing the page
  // with /edit stays open, and navigating away closes it.
  useEffect(() => {
    if (!actualProjectId) return;

    const pathname = location.pathname || "";
    // If path contains /edit or /settings at the end, show modal
    if (
      pathname.endsWith(`/${actualProjectId}/edit`) ||
      pathname.endsWith(`/${actualProjectId}/settings`) ||
      pathname.endsWith(`/project/${actualProjectId}/edit`) ||
      pathname.endsWith(`/project/${actualProjectId}/settings`) ||
      pathname.includes(`/${actualProjectId}/edit`) ||
      pathname.includes(`/${actualProjectId}/settings`)
    ) {
      setShowEditProjectModal(true);
    } else {
      setShowEditProjectModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, actualProjectId]);

  // Calculate total hours when timeframe filter changes or on mount.
  // Deferred off the critical path: the header hours badge isn't needed for
  // first paint, and the underlying query (entries?limit=1000) is heavy.
  useEffect(() => {
    const schedule =
      typeof window !== "undefined" && window.requestIdleCallback
        ? window.requestIdleCallback
        : (cb) => setTimeout(cb, 800);
    const cancel =
      typeof window !== "undefined" && window.cancelIdleCallback
        ? window.cancelIdleCallback
        : clearTimeout;
    if (!currentProject || loadingCards || loadingColumns) return undefined;
    const handle = schedule(() => calculateTotalHours(timeframeFilter));
    return () => cancel(handle);
  }, [
    timeframeFilter,
    actualProjectId,
    calculateTotalHours,
    currentProject,
    loadingCards,
    loadingColumns,
  ]);

  // Close timeframe dropdown on outside click
  useEffect(() => {
    if (!showTimeframeDropdown) return;

    const handleClickOutside = (e) => {
      if (
        timeframeDropdownRef.current &&
        !timeframeDropdownRef.current.contains(e.target)
      ) {
        setShowTimeframeDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showTimeframeDropdown]);

  if (loading || loadingCards || loadingColumns) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // if (!currentProject) {
  //   return (
  //     <div className="text-center py-12">
  //       <h3 className="text-lg font-medium text-secondary-900 mb-2">
  //         Project not found
  //       </h3>
  //       <p className="text-secondary-600 mb-6">
  //         The project you're looking for doesn't exist.
  //       </p>
  //       <Link to="/" className="btn-primary">
  //         Back to Dashboard
  //       </Link>
  //     </div>
  //   );
  // }


  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  return (
    <div className="h-full flex flex-col max-h-full">
      {/* Compact Header */}
      <div className="h-12 border border-gray-300 dark:border-slate-600 text-black  rounded-lg px-4 mb-2 flex-shrink-0 dark:bg-slate-900 dark:dark:text-white">
        <div className="h-full flex items-center justify-between gap-3">
          {/* Left: Back + title + description */}
          <div className="flex items-center gap-3 min-w-0">
            {/* <Link
              to="/"
              className="p-2 rounded-lg hover:bg-blue-500 text-white transition-colors"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link> */}
            <div className="min-w-0">
              {/* project title */}
              <h1 className="flex items-center">
                <button
                  onClick={handleEditProject}
                  className="group h-8 flex items-center gap-2.5 px-3 -ml-3 rounded-lg border border-transparent hover:border-gray-200/80 dark:hover:border-slate-700/60 hover:bg-gray-50 dark:hover:bg-slate-800/40 hover:shadow-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 active:scale-[0.98] text-left min-w-0"
                  title="Edit Project Settings"
                  aria-label={`Edit settings for project: ${currentProject.name}`}
                >
                  <span className="text-lg font-bold tracking-tight text-gray-900 dark:text-white truncate max-w-[35vw] block">
                    {currentProject.name}
                  </span>
                </button>
              </h1>
              {/* <p className="text-blue-100 text-sm truncate max-w-[50vw]">
                {stripHtmlTags(currentProject.description)}
              </p> */}
            </div>
            <span
              className={`h-6 inline-flex items-center px-2.5 rounded-lg text-[11px] font-semibold uppercase tracking-wide border cursor-default ${getProjectStatusColors(currentProject.projectStatus).bgColor
                } ${getProjectStatusColors(currentProject.projectStatus).textColor
                } ${getProjectStatusColors(currentProject.projectStatus)
                  .borderColor
                }`}
              title="Project status"
            >
              {getProjectStatusColors(currentProject.projectStatus).label}
            </span>
          </div>

          {/* Right: Members + Status pills + Date pill + Filter + Settings */}
          <div className="flex items-center gap-2 relative">
            <div className="flex flex-wrap items-center justify-center gap-2">
              {/* Card Image Visibility Toggle */}
              <div
                className="h-8 flex items-center gap-2 px-2.5 rounded-md text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700"
                title={showCardImages ? "Hide card images" : "Show card images"}
              >
                <span className="text-xs font-medium select-none">
                  {showCardImages ? "Hide Image" : "Show Image"}
                </span>
                <button
                  type="button"
                  onClick={toggleShowCardImages}
                  role="switch"
                  aria-checked={showCardImages}
                  aria-label="Toggle card image visibility"
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${
                    showCardImages ? "bg-blue-600" : "bg-gray-300 dark:bg-slate-600"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                      showCardImages ? "translate-x-4" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>

              {/* Time Period Dropdown with Total Hours Display */}
            {features.timeTracking && (
            <div ref={timeframeDropdownRef} className="relative flex items-center gap-0">
              {/* Dropdown Button */}
              <button
                onClick={() => setShowTimeframeDropdown(!showTimeframeDropdown)}
                className="h-8 flex items-center gap-2 px-2.5 rounded-l-md text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-150 border border-r-0 border-gray-200 dark:border-slate-700 group"
                title="Filter hours by timeframe"
              >
                <span className="text-xs font-medium">
                  {timeframeFilter === "thisMonth" && "This Month"}
                  {timeframeFilter === "thisWeek" && "This Week"}
                  {/* {timeframeFilter === "lastMonth" && "Last Month"}
                  {timeframeFilter === "last30Days" && "Last 30 Days"}
                  {timeframeFilter === "last90Days" && "Last 90 Days"} */}
                  {timeframeFilter === "overall" && "Overall"}
                </span>
                <svg className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 10l5 5 5-5z" />
                </svg>
              </button>

              {/* Time Display */}
              <div className="h-8 flex items-center gap-2 px-2.5 rounded-r-md text-gray-700 dark:text-slate-200 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
                <span className="text-sm font-bold tracking-tight font-mono">
                  {loadingHours ? (
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 100 16v4l3.5-3.5L12 20v4a8 8 0 01-8-8z"
                      ></path>
                    </svg>
                  ) : (
                    totalHours.formatted || "00:00:00"
                  )}
                </span>
              </div>

              {/* Dropdown Menu */}
              {showTimeframeDropdown && (
                <div className="absolute left-0 top-full mt-2 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-xl ring-1 ring-black/10 dark:ring-white/10 overflow-hidden animate-[fadeIn_120ms_ease-out] z-50">
                  <div className="divide-y divide-gray-200 dark:divide-slate-700">
                    {[
                      { value: "thisMonth", label: "This Month" },
                      { value: "thisWeek", label: "This Week" },
                      // { value: "lastMonth", label: "Last Month" },
                      // { value: "last30Days", label: "Last 30 Days" },
                      // { value: "last90Days", label: "Last 90 Days" },
                      { value: "overall", label: "Overall (All-Time)" },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setTimeframeFilter(option.value);
                          setShowTimeframeDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors duration-150 ${
                          timeframeFilter === option.value
                            ? "bg-blue-600 dark:bg-blue-700 text-white font-medium shadow-sm"
                            : "text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700/70"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{option.label}</span>
                          {timeframeFilter === option.value && (
                            <span className="opacity-80">✓</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}
              {/* Live URL */}
              {currentProject.liveSiteUrl && (
                <a
                  href={currentProject.liveSiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group h-8 flex items-center gap-1.5 px-2.5 rounded-md text-xs font-medium text-gray-700 dark:text-slate-300 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700 hover:text-blue-700 dark:hover:text-blue-400 transition-colors duration-150"
                >
                  <ExternalLink className="w-3 h-3 text-gray-400 group-hover:text-blue-500 transition-colors duration-150" />
                  <span>Live Site</span>
                </a>
              )}

              {/* Demo URLs */}
              {currentProject.demoSiteUrls && currentProject.demoSiteUrls.length > 0 && (() => {
                // Always show the last URL in the array
                const lastIndex = currentProject.demoSiteUrls.length - 1;
                const selectedUrl = currentProject.demoSiteUrls[lastIndex];

                return selectedUrl ? (
                  <a
                    href={selectedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group h-8 flex items-center gap-1.5 px-2.5 rounded-md text-xs font-medium text-gray-700 dark:text-slate-300 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700 hover:text-blue-700 dark:hover:text-blue-400 transition-colors duration-150"
                  >
                    <ExternalLink className="w-3 h-3 text-gray-400 group-hover:text-blue-500 transition-colors duration-150" />
                    <span>Demo Site</span>
                  </a>
                ) : null;
              })()}

              {/* Markup URL */}
              {currentProject.markupUrl && (
                <a
                  href={currentProject.markupUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group h-8 flex items-center gap-1.5 px-2.5 rounded-md text-xs font-medium text-gray-700 dark:text-slate-300 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700 hover:text-blue-700 dark:hover:text-blue-400 transition-colors duration-150"
                >
                  <ExternalLink className="w-3 h-3 text-gray-400 group-hover:text-blue-500 transition-colors duration-150" />
                  <span>Markup</span>
                </a>
              )}
            </div>

            <div className="w-px h-5 bg-gray-200 dark:bg-slate-700" />

            {/* Members avatar group */}
            {Array.isArray(currentProject.members) && (
              <div className="flex items-center gap-2 relative z-[20]">
                <button
                  ref={membersBtnRef}
                  onClick={() => setShowMembersPopover((s) => !s)}
                  className="flex items-center group"
                >
                  {visibleHeaderMembers.length > 0 ? (
                    <div className="-space-x-2 flex items-center">
                      {visibleHeaderMembers.map((user, idx) => (
                        <div
                          key={user._id || user.id || user.email || idx}
                          className="relative inline-flex border-2 border-white/40 rounded-full shadow-sm transition-transform group-hover:scale-[1.02] bg-white/0"
                          style={{ zIndex: 10 - idx }}
                        >
                          <Avatar user={user} size="xs" showTooltip={true} />
                        </div>
                      ))}
                      {extraMembersCount > 0 && (
                        <div
                          className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-secondary-300 text-secondary-600 text-[11px] font-semibold border-2 border-white/40 shadow-sm"
                          title={`${extraMembersCount} more member${
                            extraMembersCount === 1 ? "" : "s"
                          }`}
                        >
                          +{extraMembersCount}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="inline-flex w-6 h-6 items-center justify-center rounded-full border-2 border-white/40 bg-secondary-100 text-secondary-600 shadow-sm">
                      <UserPlus className="w-4 h-4" />
                    </div>
                  )}
                </button>

                {/* Popover listing all members */}
                {showMembersPopover && (
                  <div
                    ref={membersPopoverRef}
                    className="absolute left-0 top-full mt-2 w-80 bg-white text-secondary-900 rounded-lg shadow-lg ring-1 ring-black/5 overflow-hidden animate-[fadeIn_120ms_ease-out]"
                  >
                    <div className="px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span className="text-sm font-semibold">
                          {headerMembers.length} members
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setShowInviteModal(true);
                          setShowMembersPopover(false);
                        }}
                        className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                        title="Add members"
                      >
                        <UserPlus className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="max-h-64 overflow-auto divide-y divide-secondary-100 cursor-pinter">
                      {currentProject.members.map((m, idx) => (
                        <div
                          key={(m.user && (m.user._id || m.user.id)) || idx}
                          className="flex items-center gap-3 px-3 py-2 hover:bg-secondary-50 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Avatar user={m.user} size="sm" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {m.user?.name || m.user?.email || "Unknown"}
                            </div>
                            <div className="text-xs text-secondary-500 capitalize truncate">
                              {m.role || "member"}
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const userId = m.user?._id || m.user?.id;

                              if (userId) {
                                // handleRemoveMember(userId);
                                setConfirmingUserId(userId);
                              } else {
                                console.error(
                                  "No user ID found for member:",
                                  m
                                );
                                showToast("Unable to identify member", "error");
                              }
                            }}
                            className="p-1.5 rounded-md hover:bg-secondary-100 text-secondary-500 hover:text-red-600 transition-colors cursor-pointer"
                            title="Remove member"
                            disabled={
                              removingMemberId === (m.user?._id || m.user?.id)
                            }
                          >
                            {removingMemberId ===
                              (m.user?._id || m.user?.id) ? (
                              <svg
                                className="w-4 h-4 animate-spin"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 100 16v4l3.5-3.5L12 20v4a8 8 0 01-8-8z"
                                ></path>
                              </svg>
                            ) : (
                              <UserMinus className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Status pills */}
            <div className="flex items-center gap-2">
              {/* <span
                className={`px-3 py-1 rounded-full text-xs font-semibold border-2 shadow-sm ${
                  getProjectTypeColors(currentProject.projectType).bgColor
                } ${
                  getProjectTypeColors(currentProject.projectType).textColor
                } ${
                  getProjectTypeColors(currentProject.projectType).borderColor
                }`}
                title="Project type"
              >
                {getProjectTypeColors(currentProject.projectType).label}
              </span> */}
            </div>
            <div className="w-px h-5 bg-gray-200 dark:bg-slate-700" />
            {/* Date range */}
            {(currentProject?.startDate ||
              currentProject?.endDate) && (
                <div
                  className="h-8 flex items-center gap-1.5 px-2.5 rounded-md bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs text-gray-600 dark:text-slate-400"
                  title="Project start and end date"
                >
                  <Calendar className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500" />
                  <span className="font-medium text-gray-700 dark:text-slate-200">
                    {currentProject?.startDate
                      ? formatDate(currentProject.startDate)
                      : "—"}
                  </span>
                  <span className="text-gray-300 dark:text-slate-600">&ndash;</span>
                  <span className="font-medium text-gray-700 dark:text-slate-200">
                    {currentProject?.endDate
                      ? formatDate(currentProject.endDate)
                      : "—"}
                  </span>
                </div>
              )}

            <div className="w-px h-5 bg-gray-200 dark:bg-slate-700" />

            {/* Filter, Time Reports, Settings, AI Extract buttons */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className="h-8 w-8 flex items-center justify-center rounded-md text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-150 relative"
                title="Filter cards"
              >
                <Filter className="w-4 h-4" />
                {hasActiveFilters && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-600 rounded-full border-2 border-white dark:border-slate-900"></span>
                )}
              </button>
              {features.timeTracking && (
                <button
                  onClick={() => setShowTimeReportsModal(true)}
                  className="h-8 w-8 flex items-center justify-center rounded-md text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-150"
                  title="Time Reports"
                >
                  <BarChart3 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={handleEditProject}
                className="h-8 w-8 flex items-center justify-center rounded-md text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-150"
                title="Project Settings"
              >
                <Settings className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.set('modal', 'ai-extract');
                  navigate(`${location.pathname}?${searchParams.toString()}`, { replace: false });
                }}
                className="h-8 w-8 flex items-center justify-center rounded-md text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-150"
                title="Extract tickets from PDF using AI"
              >
                <Sparkles className="w-4 h-4" />
              </button>
            </div>
            {hasActiveFilters && (
              <button
                onClick={handleCancelFilter}
                className="h-8 rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors duration-150 flex items-center gap-1.5 px-3 ml-1.5"
                title="Cancel all filters"
              >
                <X className="w-4 h-4" />
                <span className="text-sm font-medium">Cancel Filter</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Board */}
      <div className="relative flex-1 overflow-hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(event) => {
            if (event.active.data.current?.type === "card") {
              setActiveCardId(event.active.id);
            }
          }}
          onDragEnd={handleCardDragEnd}
        >
          <div
            ref={scrollContainerRef}
            className="h-full overflow-x-auto rounded-lg scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-white dark:scrollbar-track-slate-900"
          >
            <div className="flex gap-2 h-full rounded-lg  lg:gap-[12px] min-w-max ">
              {columns.map((column) => {
                const config = getColumnConfig(column);
                const colKey = column._id || column.status;
                const columnCards = getCardsByStatus(column.status);
                return (
                  <div
                    key={colKey}
                    className={`w-80 h-full flex-shrink-0 transition-all duration-150 cursor-grab active:cursor-grabbing ${
                      draggingColumnId === colKey
                        ? "opacity-100 scale-[0.98]"
                        : "opacity-100"
                    } ${
                      dragOverColumnId === colKey
                        ? "ring-4 ring-blue-500 rounded-lg bg-blue-50 shadow-xl border-2 border-blue-300"
                        : ""
                    }`}
                    draggable={column.status !== "archive"}
                    onDragStart={() => handleColumnDragStart(colKey)}
                    onDragEnter={() => setDragOverColumnId(colKey)}
                    onDragOver={handleColumnDragOver}
                    onDragLeave={() => setDragOverColumnId(null)}
                    onDrop={() => handleColumnDrop(colKey)}
                    onDragEnd={handleColumnDragEnd}
                  >
                    <ListColumn
                      title={config.title}
                      description={column.description}
                      status={column.status}
                      cards={columnCards}
                      color={config.color}
                      bgColor={config.bgColor}
                      borderColor={config.borderColor}
                      textColor={config.textColor}
                      onCardUpdated={handleCardUpdated}
                      onCardDeleted={handleCardDeleted}
                      onCardRestored={handleCardRestored}
                      onStatusChange={handleStatusChange}
                      onCardClick={handleCardClick}
                      activeTimer={activeTimer}
                      activeTimers={activeTimers}
                      onTimerStart={handleTimerStart}
                      onTimerStop={handleTimerStop}
                      projectId={actualProjectId}
                      onColumnUpdate={handleUpdateColumn}
                      onColumnDelete={handleColumnDelete}
                      onAddCard={handleAddCardToColumn}
                      onMoveAllCards={handleMoveAllCards}
                    />
                  </div>
                );
              })}

              {/* Add Column Button - Fixed Position */}
              <div className="w-80 h-full flex-shrink-0">
                <div className="bg-gray-50 rounded-lg border-2 border-dashed dark:border-slate-600 dark:bg-slate-900 text-black dark:dark:text-white/70 h-full flex items-center justify-center hover:bg-gray-100 hover:border-gray-400 transition-all duration-200 group">
                  <button
                    onClick={() => setShowAddColumnModal(true)}
                    className="flex flex-col items-center space-y-3 text-gray-500 hover:text-gray-700 transition-colors duration-200 group-hover:scale-105"
                  >
                    <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-slate-900 dark:border dark:border-white/40 dark:group-hover:border-none group-hover:bg-gray-300 dark:group-hover:bg-slate-700 flex items-center justify-center transition-colors duration-200">
                      <Plus className="w-6 h-6" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium">Add Column</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Create a new workflow stage
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Drag Overlay for visual feedback */}
          <DragOverlay>
            {activeCardId ? (
              <div className="bg-white rounded-lg shadow-xl border-2 border-blue-500 p-3 w-80 opacity-95 rotate-3 transform">
                <div className="text-sm font-semibold text-gray-800">
                  {cards.find((c) => c._id === activeCardId)?.title || "Card"}
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Jira-style Horizontal Scroll Indicator (self-subscribes to scroll) */}
        <HorizontalScrollIndicator
          containerRef={scrollContainerRef}
          revision={columns.length}
          indicatorWidth={150}
          onCanScrollMoreChange={handleCanScrollMoreChange}
        />

        {/* Scroll to end button */}
        {/* {showScrollButton && (
          <button
            onClick={scrollToEnd}
            className="absolute bottom-12 right-6 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors duration-200 z-10"
            title="Scroll to end"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )} */}
      </div>

      {/* Create Card Modal */}
      {showCreateModal && (
        <CreateCardModal
          projectId={id}
          onClose={() => setShowCreateModal(false)}
          onCardCreated={handleCardCreated}
          defaultStatus={selectedStatus}
        />
      )}

      {/* Add Column Modal */}
      {showAddColumnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={addColumnModalRef}
            className="bg-white w-[35rem] rounded-lg max-w-lg mx-auto dark:bg-slate-800 shadow-lg transition-transform transform scale-100 animate-[fadeInScale_150ms_ease-out]"
          >
            <div className="bg-[#f1f2f4] rounded-t-xl dark:bg-slate-900 p-4 flex-shrink-0 transition-colors duration-200">
              <div className="flex items-center space-x-4">
                <div>
                  <h2 className="text-base font-semibold dark:text-white">Add New Column</h2>
                </div>
              </div>
            </div>
            <div className="space-y-4 p-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Column Name
                </label>
                <input
                  type="text"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  placeholder="Enter column name"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Description
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <SimpleQuillEditor
                    value={newColumnDescription}
                    onChange={setNewColumnDescription}
                    placeholder="Enter column description (optional)"
                    height="100px"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {["blue", "green", "yellow", "red", "purple", "pink", "indigo", "orange", "gray"].map(
                    (color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setNewColumnColor(color)}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          newColumnColor === color
                            ? "border-gray-900 dark:border-white scale-110 shadow-sm"
                            : "border-transparent hover:scale-105 opacity-70 hover:opacity-100"
                        }`}
                        style={{
                          backgroundColor:
                            color === 'gray' ? '#6b7280' :
                            color === 'blue' ? '#3b82f6' :
                            color === 'green' ? '#22c55e' :
                            color === 'yellow' ? '#eab308' :
                            color === 'red' ? '#ef4444' :
                            color === 'purple' ? '#a855f7' :
                            color === 'pink' ? '#ec4899' :
                            color === 'indigo' ? '#6366f1' :
                            color === 'orange' ? '#f97316' : '#6b7280'
                        }}
                        title={color.charAt(0).toUpperCase() + color.slice(1)}
                      />
                    )
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 p-6">
              <button
                onClick={() => {
                  setShowAddColumnModal(false);
                  setNewColumnName("");
                  setNewColumnDescription("");
                  setNewColumnColor("gray");
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors dark:text-gray-400 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleAddColumn}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Create Column
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Column Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setColumnToDelete(null);
        }}
        onConfirm={confirmDeleteColumn}
        title="Delete Column"
        message={
          columnToDelete
            ? `Are you sure you want to delete the "${columnToDelete.name}" column? All cards in this column will be moved to "To Do".`
            : "Are you sure you want to delete this column?"
        }
        confirmText="Delete Column"
        cancelText="Cancel"
        type="danger"
        isLoading={false}
      />
      <ConfirmationModal
        isOpen={!!confirmingUserId}
        title="Remove Member"
        message="Are you sure you want to Delete user"
        onCancel={() => setConfirmingUserId(null)}
        onConfirm={() => {
          handleRemoveMember(confirmingUserId);
          setConfirmingUserId(null);
        }}
        onClose={() => {
          setConfirmingUserId(false);
        }}
      />

      {/* Card Modal */}
      {showCardModal && selectedCard && (
        <CardModal
          card={selectedCard}
          cards={filteredCards}
          onClose={handleCardModalClose}
          onCardUpdated={handleCardUpdated}
          onCardDeleted={handleCardDeleted}
          onCardRestored={handleCardRestored}
          onCardPermanentlyDeleted={handleCardPermanentlyDeleted}
          onStatusChange={handleStatusChange}
          onNavigateCard={handleNavigateCard}
          activeTimer={activeTimer}
          activeTimers={activeTimers}
          onTimerStart={handleTimerStart}
          onTimerStop={handleTimerStop}
        />
      )}

      {/* Edit Project Modal */}
      {showEditProjectModal && currentProject && (
        <EditProjectModal
          project={currentProject}
          onClose={() => {
            setShowEditProjectModal(false);
            // Ensure we return to the main project view path when modal closes
            navigate(`/project/${actualProjectId}`);
          }}
        />
      )}

      {/* Invite/Add Members Modal */}
      {showInviteModal && currentProject && (
        <InviteUserModal
          project={currentProject}
          onClose={() => setShowInviteModal(false)}
          onUserInvited={() => fetchProject(currentProject._id)}
        />
      )}

      {/* Filter Panel */}
      <FilterPanel
        key={filterResetKey}
        isOpen={showFilterPanel}
        onClose={() => setShowFilterPanel(false)}
        cards={cards}
        onFilterChange={(filtered, active) => {
          setFilteredCards(filtered);
          setHasActiveFilters(typeof active === 'boolean' ? active : filtered.length !== cards.length);
        }}
        columns={columns}
        project={currentProject}
      />

      {/* Move All Cards Modal */}
      {showMoveAllCardsModal && sourceColumnForMove && (
        <MoveAllCardsModal
          isOpen={showMoveAllCardsModal}
          onClose={() => {
            setShowMoveAllCardsModal(false);
            setSourceColumnForMove(null);
          }}
          onConfirm={handleConfirmMoveAllCards}
          sourceColumn={sourceColumnForMove}
          columns={columns}
          cardCount={getCardsByStatus(sourceColumnForMove.status).length}
          isLoading={isMovingCards}
        />
      )}

      {/* Time Reports Modal */}
      {features.timeTracking && (
        <TimeReportsModal
          isOpen={showTimeReportsModal}
          onClose={() => setShowTimeReportsModal(false)}
          projectId={currentProject?._id}
          projectName={currentProject?.name}
        />
      )}

      {/* AI Extract Modal */}
      {showAIExtractModal && currentProject && (
        <AIExtractModal
          projectId={currentProject._id}
          onClose={() => {
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete('modal');
            const newSearch = searchParams.toString();
            navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: false });
          }}
          onCardsCreated={(cards) => {
            // Refresh cards after creation
            fetchCards();
          }}
        />
      )}
    </div>
  );
};

export default ProjectBoard;





